/**
 * Search History Management Utility
 * Manages session storage for keywords and companies across the application
 */

const STORAGE_KEY = 'search-history';
const MAX_HISTORY_ITEMS = 50;

/**
 * Dispatch a custom event to notify components about history updates
 */
const dispatchHistoryUpdate = () => {
    window.dispatchEvent(new CustomEvent('searchHistoryUpdated'));
};

/**
 * Get the current search history from session storage
 * @returns {Object} History object with keywords and companies arrays
 */
export const getSearchHistory = () => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return { keywords: [], companies: [] };
        }
        
        const parsed = JSON.parse(stored);
        return {
            keywords: Array.isArray(parsed.keywords) ? parsed.keywords : [],
            companies: Array.isArray(parsed.companies) ? parsed.companies : []
        };
    } catch (error) {
        console.error('Error reading search history:', error);
        return { keywords: [], companies: [] };
    }
};

/**
 * Save search history to session storage
 * @param {Object} history - History object with keywords and companies
 */
const saveSearchHistory = (history) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
        dispatchHistoryUpdate(); // Notify components of the update
    } catch (error) {
        console.error('Error saving search history:', error);
    }
};

/**
 * Add a keyword to search history
 * @param {Object} keyword - Keyword object with id, keyword_name, etc.
 * @param {string} source - Source of the keyword ('direct-search', 'autocomplete', 'related-click')
 */
export const addKeywordToHistory = (keyword, source = 'unknown') => {
    if (!keyword || !keyword.keyword_name) {
        console.warn('Invalid keyword provided to addKeywordToHistory:', keyword);
        return;
    }

    const history = getSearchHistory();
    const timestamp = Date.now();
    
    // Create normalized keyword object
    const normalizedKeyword = {
        id: keyword.keyword_id || `temp-${keyword.keyword_name.toLowerCase().replace(/\s+/g, '-')}`,
        keyword_name: keyword.keyword_name,
        timestamp,
        source,
        // Keep any additional properties
        ...keyword
    };

    // Remove existing entry with same keyword_name (case-insensitive)
    history.keywords = history.keywords.filter(
        k => k.keyword_name.toLowerCase() !== keyword.keyword_name.toLowerCase()
    );

    // Add to beginning of array (most recent first)
    history.keywords.unshift(normalizedKeyword);

    // Limit history size
    if (history.keywords.length > MAX_HISTORY_ITEMS) {
        history.keywords = history.keywords.slice(0, MAX_HISTORY_ITEMS);
    }

    saveSearchHistory(history);
    console.log('Added keyword to history:', normalizedKeyword.keyword_name);
};

/**
 * Add a company to search history
 * @param {Object} company - Company object with company_id, symbol, name, etc.
 * @param {string} source - Source of the company ('autocomplete', 'related-click', 'price-load')
 */
export const addCompanyToHistory = (company, source = 'unknown') => {
    if (!company || (!company.company_id && !company.symbol)) {
        console.warn('Invalid company provided to addCompanyToHistory:', company);
        return;
    }

    const history = getSearchHistory();
    const timestamp = Date.now();
    
    // Create normalized company object
    const normalizedCompany = {
        company_id: company.company_id || `temp-${company.symbol}`,
        symbol: company.symbol || company.name,
        name: company.name || company.companyname || company.symbol,
        timestamp,
        source,
        // Keep any additional properties
        ...company
    };

    // Remove existing entry with same company_id or symbol
    history.companies = history.companies.filter(
        c => c.company_id !== normalizedCompany.company_id && 
             c.symbol !== normalizedCompany.symbol
    );

    // Add to beginning of array (most recent first)
    history.companies.unshift(normalizedCompany);

    // Limit history size
    if (history.companies.length > MAX_HISTORY_ITEMS) {
        history.companies = history.companies.slice(0, MAX_HISTORY_ITEMS);
    }

    saveSearchHistory(history);
    console.log('Added company to history:', normalizedCompany.symbol);
};

/**
 * Get recent items from history
 * @param {string} type - 'keywords' or 'companies'
 * @param {number} limit - Maximum number of items to return
 * @returns {Array} Array of recent items
 */
export const getRecentItems = (type, limit = 10) => {
    const history = getSearchHistory();
    const items = history[type] || [];
    
    return items
        .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
        .slice(0, limit);
};

/**
 * Remove a specific item from history
 * @param {string} type - 'keywords' or 'companies'
 * @param {string} id - ID of item to remove (keyword_id or company_id)
 */
export const removeFromHistory = (type, id) => {
    const history = getSearchHistory();
    
    if (type === 'keywords') {
        history.keywords = history.keywords.filter(k => k.id !== id);
    } else if (type === 'companies') {
        history.companies = history.companies.filter(c => c.company_id !== id);
    }
    
    saveSearchHistory(history);
    console.log(`Removed ${type} item with id ${id} from history`);
};

/**
 * Clear all search history
 */
export const clearHistory = () => {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
        dispatchHistoryUpdate(); // Notify components of the update
        console.log('Search history cleared');
    } catch (error) {
        console.error('Error clearing search history:', error);
    }
};

/**
 * Get total count of items in history
 * @returns {Object} Object with keywords and companies counts
 */
export const getHistoryCount = () => {
    const history = getSearchHistory();
    return {
        keywords: history.keywords.length,
        companies: history.companies.length,
        total: history.keywords.length + history.companies.length
    };
};

/**
 * Check if an item exists in history
 * @param {string} type - 'keywords' or 'companies'
 * @param {string} identifier - keyword_name or company symbol/id
 * @returns {boolean} True if item exists in history
 */
export const isInHistory = (type, identifier) => {
    const history = getSearchHistory();
    
    if (type === 'keywords') {
        return history.keywords.some(k => 
            k.keyword_name.toLowerCase() === identifier.toLowerCase()
        );
    } else if (type === 'companies') {
        return history.companies.some(c => 
            c.symbol === identifier || c.company_id === identifier
        );
    }
    
    return false;
}; 